import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";
import { useState } from "react";

const CAREERS_FORM_URL = "https://readdy.ai/api/form/dabf64sldn1crcvmign0";

const whyWorkItems = [
  {
    title: "Mission-Critical Work",
    description: "Support enterprise IT environments for federal agencies and commercial organizations where technology directly impacts mission outcomes.",
    icon: "ri-shield-check-line",
  },
  {
    title: "Specialized Technology",
    description: "Work with enterprise infrastructure, databases, virtualization, and platform technologies in complex, high-visibility environments.",
    icon: "ri-server-line",
  },
  {
    title: "Government Programs",
    description: "Gain experience in federal enterprise IT programs through established teaming relationships.",
    icon: "ri-government-line",
  },
  {
    title: "Professional Growth",
    description: "Develop expertise in enterprise technology environments, working alongside experienced engineers, administrators, and technical SMEs.",
    icon: "ri-star-line",
  },
];

export default function Careers() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim().length > 0) {
      setFormStatus("success");
      form.reset();
      return;
    }

    setFormStatus("submitting");
    setFormError("");

    // Empty honeypot fields are not included in the request payload.
    formData.delete("website_alt");

    try {
      const res = await fetch(CAREERS_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        setFormError(serverMsg || "There was a problem submitting your resume. Please try again.");
        setFormStatus("idle");
      }
    } catch {
      setFormError("There was a problem submitting your resume. Please try again.");
      setFormStatus("idle");
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-md border border-background-200/80 bg-white text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100";
  const labelClass = "block text-sm font-medium text-foreground-700 mb-1.5";

  return (
    <Layout>
      <PageHero
        title="Careers at Mitaja"
        subtitle="Join Our Team"
        description="Work with enterprise technology professionals on mission-critical government and commercial programs."
        size="lg"
      />

      <section className="section-padding bg-white">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
            <div>
              <SectionHeader
                title="Work at Mitaja"
                description="Join a team of technology professionals delivering enterprise infrastructure, database services, and specialized IT support for government and commercial programs."
                centered={false}
              />
              <div className="space-y-6">
                {whyWorkItems.map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-primary-600 text-xl`} aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-foreground-900 mb-1">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="card-base p-8">
                <h3 className="text-xl font-bold text-foreground-900 mb-2">
                  Current Opportunities
                </h3>
                <p className="text-sm text-foreground-500 mb-6">
                  Check back for open positions. Mitaja is regularly seeking qualified technical professionals for federal and commercial programs.
                </p>
                <div className="space-y-3">
                  <div className="p-4 bg-background-50 rounded-lg border border-background-200/80">
                    <p className="text-sm font-semibold text-foreground-700 mb-1">
                      Network Engineers
                    </p>
                    <p className="text-xs text-foreground-500">
                      Federal enterprise IT programs
                    </p>
                  </div>
                  <div className="p-4 bg-background-50 rounded-lg border border-background-200/80">
                    <p className="text-sm font-semibold text-foreground-700 mb-1">
                      Database Engineers / Administrators
                    </p>
                    <p className="text-xs text-foreground-500">
                      Oracle, SQL Server environments
                    </p>
                  </div>
                  <div className="p-4 bg-background-50 rounded-lg border border-background-200/80">
                    <p className="text-sm font-semibold text-foreground-700 mb-1">
                      Technical SMEs
                    </p>
                    <p className="text-xs text-foreground-500">
                      Enterprise infrastructure and platform support
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="max-w-2xl mx-auto">
            <div className="card-base p-8">
              <h3 className="text-xl font-bold text-foreground-900 mb-2">
                Submit Your Resume
              </h3>
              <p className="text-sm text-foreground-500 mb-2">
                Interested in joining Mitaja? Submit your resume and we will contact you when relevant opportunities arise.
              </p>
              <p className="text-xs text-foreground-400 mb-6">
                Fields marked with an asterisk (<span aria-hidden="true">*</span>) are required.
              </p>
              {formStatus === "success" ? (
                <div
                  role="status"
                  aria-live="polite"
                  className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-center"
                >
                  <i className="ri-check-line text-accent-600 text-2xl mb-2" aria-hidden="true"></i>
                  <p className="text-sm font-semibold text-accent-700">
                    Thank you for your interest. We will review your submission and contact you if there is a match.
                  </p>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                  data-readdy-form="careers-resume"
                  noValidate
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="career-firstName" className={labelClass}>
                        First Name <span aria-hidden="true" className="text-red-600">*</span>
                      </label>
                      <input
                        id="career-firstName"
                        type="text"
                        name="firstName"
                        required
                        aria-required="true"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="career-lastName" className={labelClass}>
                        Last Name <span aria-hidden="true" className="text-red-600">*</span>
                      </label>
                      <input
                        id="career-lastName"
                        type="text"
                        name="lastName"
                        required
                        aria-required="true"
                        className={inputClass}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="career-email" className={labelClass}>
                      Email <span aria-hidden="true" className="text-red-600">*</span>
                    </label>
                    <input
                      id="career-email"
                      type="email"
                      required
                      aria-required="true"
                      name="email"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="career-position" className={labelClass}>
                      Position of Interest
                    </label>
                    <select
                      id="career-position"
                      name="position"
                      className={inputClass}
                    >
                      <option value="">Select a position</option>
                      <option value="network-engineer">Network Engineer</option>
                      <option value="database-engineer">Database Engineer / Administrator</option>
                      <option value="technical-sme">Technical SME</option>
                      <option value="developer">Developer</option>
                      <option value="analyst">Analyst</option>
                      <option value="general">General Interest</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="career-message" className={labelClass}>
                      Message / Cover Letter
                    </label>
                    <textarea
                      id="career-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className={`${inputClass} resize-none`}
                      placeholder="Briefly describe your experience and the role you are interested in..."
                    />
                    <p className="text-xs text-foreground-400 mt-1">
                      Maximum 500 characters
                    </p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  {formError && (
                    <div role="alert" className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">{formError}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="btn-primary w-full sm:w-auto disabled:opacity-50"
                  >
                    {formStatus === "submitting" ? "Submitting..." : "Submit Resume"}
                    <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <CTAPanel
        title="Have Questions About Careers?"
        description="Contact us for more information about working at Mitaja Corporation."
        primaryAction={{ label: "Contact Us", href: "/contact" }}
        variant="dark"
      />
    </Layout>
  );
}